<!--
  //app/components/pages/teams-team/TeamSeasonsFilter.vue
  -->

<script setup lang="ts">
import type { Season } from "~/src/season-cluster/domain/models/01.Season";
import type { RegionOption } from "~/src/region/domain/models/Region";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useSeasonCascade } from "~/app/composables/season/useSeasonCascade";
import { useTeams } from "~/app/composables/teams";
import { useWebContext } from "~/app/composables/webContext";
import RegionUiService from "~/src/region/infrastructure/ui-services/regionUiService";
import { SHARED_COMPETITIONS } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

type Props = {
  combinedWithMoreFilters?: boolean;
  teamId: number;
  teamCountryId?: number;
  teamSeasons: Season[] | undefined;
  initialFilterValues?: { seasonId: number; seasonClusterId: number; regionId: number };
};

const props = withDefaults(defineProps<Props>(), {
  combinedWithMoreFilters: false,
});

const emit = defineEmits(["change"]);

const { changeSeason } = useSeasonFilter();
const { findLastSeasonClusterForRegion, findLastSeasonInCluster } = useSeasonCascade();
const { findTeamCompetitionsIds } = useTeams();
const { findRegionInfoByRegionId, findRegionInfoByCompetitionId } = RegionUiService();
const { webContext } = useWebContext();

const allRegionOptions: RegionOption[] = props.teamSeasons
  ? await findTeamCompetitionsIds(props.teamSeasons)
  : [];

// Filter regions with shared competitions based on teamCountryId
const regionOptions = computed((): RegionOption[] => {
  if (!props.teamCountryId) {
    // If we don't have teamCountryId, return all regions (default behavior)
    return allRegionOptions;
  }

  const filtered = allRegionOptions.filter(region => {
    // Check if this region has any shared competition
    const hasSharedCompetitions = region.competitionIds.some(compId => SHARED_COMPETITIONS.includes(compId));

    // If it has shared competitions, only show if apiId matches teamCountryId
    if (hasSharedCompetitions) {
      const regionApiId = findRegionInfoByRegionId(region.id, "apiId");
      return regionApiId === props.teamCountryId;
    }

    // If it doesn't have shared competitions, always show
    return true;
  });

  // If current regionId is not in filtered options, find equivalent region
  nextTick(() => {
    if (regionId.value && filtered.length > 0) {
      const currentRegionExists = filtered.some(r => r.id === regionId.value);
      if (!currentRegionExists) {
        // Find the original region that was filtered out
        const filteredRegion = allRegionOptions.find(r => r.id === regionId.value);

        if (filteredRegion) {
          // Find a non-filtered region that has a competition in common
          const equivalentRegion = filtered.find(r =>
            r.competitionIds.some(compId => filteredRegion.competitionIds.includes(compId))
          );

          regionId.value = equivalentRegion ? equivalentRegion.id : filtered[0].id;
        } else {
          // If we can't find the original region, select the first available
          regionId.value = filtered[0].id;
        }
      }
    }
  });

  return filtered;
});

const teamSeasons = props.teamSeasons || ([] as Season[]);

const regionId = defineModel<number>("regionId");
const seasonClusterId = defineModel<number>("seasonClusterId");
const seasonId = defineModel<number>("seasonId");

const seasonOptions = computed((): Season[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId.value, "competitionId");
  return teamSeasons
    .filter(s => {
      return (
        regionCompetitions &&
        regionCompetitions.includes(s.competitionId) &&
        s.seasonClusterId === seasonClusterId.value &&
        s.competition.visible
      );
    })
    .sort((a, b) => {
      return a.start > b.start ? 1 : a.start < b.start ? -1 : 0;
    });
});

const { data: seasonClustersAvailable } = await useFetch("/api/v1/competition/season-clusters/available");

const seasonClusterOptions = computed((): { name: string; id: number }[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId.value, "competitionId");
  const validSeasons = teamSeasons.filter(s => {
    return regionCompetitions && regionCompetitions.includes(s.competitionId) && s.competition.visible;
  });

  const seasonClusterIds = [...new Set(validSeasons.map(s => s.seasonClusterId))];

  const matchingClusters = seasonClustersAvailable.value.filter(
    cluster => seasonClusterIds.includes(cluster.id) && cluster.regionIds.includes(regionId.value)
  );

  const simplifiedOptions = matchingClusters.map(cluster => ({
    name: cluster.name,
    id: cluster.id,
  }));

  return simplifiedOptions.length ? simplifiedOptions : undefined;
});

/**
 * Helper function to filter team seasons by region and cluster
 */
const filterSeasonsByRegionAndCluster = (
  seasons: Season[],
  seasonClusterId: number,
  regionId: number
): Season[] => {
  const regionCompetitions = findRegionInfoByRegionId(regionId, "competitionId");
  return seasons.filter(
    season =>
      season.seasonClusterId === seasonClusterId &&
      regionCompetitions &&
      regionCompetitions.includes(season.competitionId) &&
      season.competition.visible
  );
};

/**
 * Computed: Last available season cluster ID for current region
 */
const lastSeasonClusterIdForRegion = computed((): number | undefined => {
  if (!seasonClustersAvailable.value || !regionId.value) {
    return undefined;
  }

  return findLastSeasonClusterForRegion(
    seasonClustersAvailable.value,
    regionId.value,
    teamSeasons,
    filterSeasonsByRegionAndCluster
  );
});

/**
 * Handles user clicking a season button
 */
const handleSeasonClick = (seasonClusterId: number, seasonIdArg: number) => {
  changeSeason(seasonClusterId, seasonIdArg, regionId.value, props.initialFilterValues);
  seasonId.value = seasonIdArg;
  emit("change", seasonId.value);
};

/**
 * Watch regionId changes - automatically select last cluster and season
 * Uses guard to prevent running on initial mount (props handle that)
 */
let isInitialized = false;

watch(regionId, (newRegion, oldRegion) => {
  // Skip if not initialized yet or no actual change
  if (!isInitialized || newRegion === oldRegion || !newRegion) return;

  const newSeasonClusterId = lastSeasonClusterIdForRegion.value;
  if (!newSeasonClusterId) return;

  seasonClusterId.value = newSeasonClusterId;

  // Cascade to last season in the new cluster
  nextTick(() => {
    const lastSeason = findLastSeasonInCluster(seasonOptions.value);
    if (lastSeason) {
      handleSeasonClick(newSeasonClusterId, lastSeason.id);
    }
  });
});

/**
 * Watch seasonClusterId changes - automatically select last season
 * Uses guard to prevent unnecessary triggers
 */
watch(seasonClusterId, (newCluster, oldCluster) => {
  // Skip if not initialized yet or no actual change
  if (!isInitialized || newCluster === oldCluster || !newCluster) return;

  nextTick(() => {
    const lastSeason = findLastSeasonInCluster(seasonOptions.value);
    if (lastSeason) {
      handleSeasonClick(newCluster, lastSeason.id);
    }
  });
});

/**
 * Initialize on mount: set initial cluster and season if not already set
 * This ensures props initialFilterValues are used first, then cascade logic
 */
onMounted(() => {
  // Set initial values if not provided via props
  if (!props.initialFilterValues) {
    const initialClusterId = lastSeasonClusterIdForRegion.value;
    if (initialClusterId && !seasonClusterId.value) {
      seasonClusterId.value = initialClusterId;

      const initialSeason = findLastSeasonInCluster(seasonOptions.value);
      if (initialSeason && !seasonId.value) {
        seasonId.value = initialSeason.id;
      }
    }
  }

  // Mark as initialized after setup to enable watchers
  nextTick(() => {
    isInitialized = true;
  });
});
</script>

<template>
  <div class="flex flex-col gap-2 md:flex-row md:flex-end justify-end my-6">
    <div
      v-if="seasonOptions.length"
      :class="['flex rounded-md shadow-sm justify-end', combinedWithMoreFilters ? 'max-w-max' : '']"
    >
      <!--    Seasons -->
      <button
        v-for="(season, index) in seasonOptions"
        :key="index"
        @click="handleSeasonClick(season.seasonClusterId, season.id)"
        :class="[
          'kql-season-button',
          {
            '!bg-primary !text-black': season.id === seasonId,
          },
        ]"
      >
        {{ season.displayName }}
      </button>

      <!-- Clusters -->
      <select
        id="seasonCluster"
        v-model="seasonClusterId"
        class="kql-select text-[10px] bigsm:text-xs lg:text-sm border-s rounded-e-lg min-w-min lg:min-w-28"
      >
        <option
          v-for="seasonCluster in seasonClusterOptions"
          :key="seasonCluster.id"
          :value="seasonCluster.id"
        >
          {{ seasonCluster.name }}
        </option>
      </select>
    </div>

    <div v-else class="flex px-4 lg:px-0 shadow-sm justify-end">
      <div class="h-10 w-full max-w-xl bg-gray-800 rounded-lg animate-pulse"></div>
    </div>

    <!--    Regions-->
    <select
      v-if="!!regionId && regionOptions.length > 0"
      id="regionOptions"
      class="kql-select order-first md:order-last md:ms-6 text-[10px] bigsm:text-xs lg:text-sm border-s rounded-lg min-w-32"
      v-model="regionId"
    >
      <option v-for="(region, index) in regionOptions" :key="index" :value="region.id">
        {{ region.name }}
      </option>
    </select>
  </div>
</template>

<style scoped></style>
